import React, { useState, useEffect, useCallback } from 'react';
import {
    Table,
    Card,
    Select,
    Row,
    Col,
    Empty,
    Skeleton,
    Input,
    Button,
    Modal,
    Upload,
    message
} from 'antd';
import { IconButton } from '../components/Buttons/buttons';
import { EyeFilled, DownOutlined, SearchOutlined ,PlusOutlined,EditOutlined,UploadOutlined,DeleteOutlined} from '@ant-design/icons';
import * as InstallationGuide from '../services/InstallationGuide';
import { useDispatch, useSelector } from 'react-redux';

const { Option } = Select;

function InstallationGuideDataTables(props) {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [guidelines, setGuidelines] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [isActive, setIsActive] = useState(false);
    const [searchedText, setSearchedText] = useState('');

    // Filter state variables
    const [manufacturer, setManufacturer] = useState('');
    const [style, setStyle] = useState('');
    const [year, setYear] = useState('');
    const [month, setMonth] = useState('');

    // State for Ant Design's Select options
    const [manufacturerOptions, setManufacturerOptions] = useState([]);
    const [styleOptions, setStyleOptions] = useState([]);
    const [yearOptions, setYearOptions] = useState([]);
    const [dependentOptionsLoading, setDependentOptionsLoading] = useState(false);

    // Debounce the search input to avoid excessive API calls
    const [debouncedSearchedText, setDebouncedSearchedText] = useState(searchedText);

     // modal states
    const [modalVisible, setModalVisible] = useState(false);
    const [editRecord, setEditRecord] = useState(null);

     const [loadings, setLoadings] = useState([]);
     const [manufacturerName, setManufacturerName] = useState('');
     const [styleName, setStyleName] = useState("");
     const [FormYear, setFormYear] = useState("");
     const [file, setFile] = useState(null);
     const [fileList, setFileList] = useState([]);
     const [refreshKey, setRefreshKey] = useState(0);
     const [FormMonth, setFormMonth] = useState('');
     const [manufacturerError, setManufacturerError] = useState('');
     const [fileError, setFileError] = useState('');
     const [fileChanged, setFileChanged] = useState(false);
    

    const userDetails = useSelector((state) => state.User.userDetails);
    const { is_admin } = userDetails || {};

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchedText(searchedText);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchedText]);

    // This effect fetches static filter options (manufacturers) once on mount.
    useEffect(() => {
        const fetchStaticOptions = async () => {
            try {
                const manufacturers = await dispatch(InstallationGuide.getManufacturer());
                setManufacturerOptions(manufacturers);
            } catch (err) {
                console.error("Failed to fetch static filter options:", err);
            }
        };

        fetchStaticOptions();
        window.scrollTo(0, 0);

    }, [dispatch]);

    // 1. This effect fetches styles based on the selected manufacturer.
    useEffect(() => {
        // Reset style and year when manufacturer changes
        setStyle('');
        setYear('');
        setMonth('');
        setStyleOptions([]);
        setYearOptions([]);

        if (manufacturer) {
            setDependentOptionsLoading(true);
            const fetchStyles = async () => {
                try {
                    const styles = await dispatch(InstallationGuide.getStyles({ manufacturer }));
                    const formattedStyles = styles.map(styleName => ({ id: styleName, name: styleName }));
                    setStyleOptions(formattedStyles);
                } catch (err) {
                    console.error("Failed to fetch dependent options:", err);
                } finally {
                    setDependentOptionsLoading(false);
                }
            };
            fetchStyles();
        }
    }, [manufacturer, dispatch]);

    // 2. This effect fetches years based on the selected manufacturer and style.
    useEffect(() => {
        setYear('');
        setYearOptions([]);

        if (manufacturer && style) {
            setDependentOptionsLoading(true);
            const fetchYears = async () => {
                try {
                    const years = await dispatch(InstallationGuide.getYears({ manufacturer, style }));
                    setYearOptions(years);
                } catch (err) {
                    console.error("Failed to fetch years:", err);
                } finally {
                    setDependentOptionsLoading(false);
                }
            };
            fetchYears();
        } else if (manufacturer && !style) { // Case where manufacturer is selected, but style is not
            setDependentOptionsLoading(true);
            const fetchYears = async () => {
                try {
                    const years = await dispatch(InstallationGuide.getYears({ manufacturer, style }));
                    setYearOptions(years);
                } catch (err) {
                    console.error("Failed to fetch years:", err);
                } finally {
                    setDependentOptionsLoading(false);
                }
            };
            fetchYears();
        }
    }, [manufacturer, style, dispatch]);

    // Use useCallback to memoize the data fetch function
    const fetchGuidelines = useCallback(async () => {
        setLoading(true);
        // Build the params object to avoid sending empty values
        const params = {};
        if (manufacturer) {
            params.manufacturer = manufacturer;
        }
        if (style) {
            params.style = style;
        }
        if (year) {
            params.year = year;
        }
        if (month) {
            params.month = month;
        }
        if (is_admin && debouncedSearchedText) {
            params.search = debouncedSearchedText;
        }

        // Only fetch data if the user is an admin or if they have selected at least one filter option
        if (is_admin || Object.keys(params).length > 0) {
            try {
                const fetchedGuidelines = await dispatch(InstallationGuide.getGuidelines(params));
                setGuidelines(fetchedGuidelines);
            } catch (err) {
                console.error("Failed to fetch guidelines:", err);
                setGuidelines([]);
            } finally {
                setLoading(false);
            }
        } else {
            // For non-admins with no filters selected, show an empty table with a specific message.
            setGuidelines([]);
            setLoading(false);
        }
    }, [dispatch, manufacturer, style, year, month, debouncedSearchedText, is_admin]);

    // This effect runs the main data fetch whenever a filter value changes.
    useEffect(() => {
        fetchGuidelines();
    }, [fetchGuidelines]);

    const handleClick = () => {
        setIsActive(!isActive);
    };

    const handleReset = () => {
        setManufacturer('');
        setStyle('');
        setYear('');
        setMonth('');
        setSearchedText('');
    };

    const columns = [
        {
            title: 'S.No.',
            dataIndex: 'index',
            key: 'index',
            render: (value, item, index) => <>{(page - 1) * pageSize + index + 1}</>,
        },
        {
            title: 'Manufacturer',
            dataIndex: 'manufacturer',
            key: 'manufacturer'
        },
        {
            title: 'Style Name/Number',
            dataIndex: 'style_number',
            key: 'style_number'
        },
        {
            title: 'Month',
            dataIndex: 'month',
            key: 'month',
            render: (value) => {
                const monthNames = [
                    'January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'
                ];

                const monthIndex = parseInt(value, 10) - 1;
                return monthNames[monthIndex] || '-';
            }
        },

        {
            title: 'Year',
            dataIndex: 'year',
            key: 'year'
        },
        {
            title: 'File',
            dataIndex: 's3_bucket_path',
            key: 's3_bucket_path',
            render: (value) => {
                if (!value) return "-";
                const fileName = value.split('/').pop();
                return (
                    <a href={value} target="_blank" rel="noopener noreferrer">
                        {fileName || "Open PDF"}
                    </a>
                );
            },
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <div className="actionBtn">
                    <IconButton
                        icon={<EyeFilled />}
                        type="primary"
                        tooltip="View Guideline"
                        text="View"
                        handleClick={() => {
                            if (record.s3_bucket_path) {
                                window.open(record.s3_bucket_path, "_blank");
                            }
                        }}
                    />
                    {is_admin && (
                        <>
                            <IconButton
                                icon={<DeleteOutlined/>}
                                text="Delete"
                                handleClick={() =>handleDelete(record)}
                            />
                            {/* <IconButton
                                icon={<EditOutlined />}
                                text="Edit"
                                handleClick={() => handleEdit(record)}
                            /> */}
                        </>
                    )}
                </div>
            ),
        },
        ].filter((item) => !item.hidden);

        const skeletonColumns = columns.map(column => ({
            ...column,
            render: () => (
                <Skeleton.Input
                    style={{ width: '100%', minWidth: '50px' }}
                    active
                />
            ),
        }));

    const handleSubmit = async () => {
        setLoading(true);
        let valid = true;

        setManufacturerError('');
        setFileError('');

        if (!manufacturerName.trim()) {
            setManufacturerError('Manufacturer name is required.');
            valid = false;
        }

        if (!editRecord && !file) {
            setFileError('PDF, DOC or DOCX file is required.');
            valid = false;
        } else if (editRecord && !editRecord.s3_bucket_path && !file) {
            setFileError('PDF, DOC or DOCX file is required.');
            valid = false;
        }else if (editRecord && fileChanged && !file) {
            setFileError("PDF, DOC or DOCX file is required.");
            setLoading(false);
            return;
        }


        if (!valid) {
            message.error("Please fill all required fields.");
            setLoading(false); 
            return;
        }

        try {
            const formData = new FormData();
            formData.append('manufacturer', manufacturerName);
            formData.append('style_number', styleName);
            formData.append('year', FormYear);
            formData.append('month', FormMonth);

            if (file && file instanceof File) {
                formData.append('guideline_file', file);
            } else if (editRecord && !fileChanged) {
                console.warn("Keeping existing file - no new file uploaded");
            }else if (file) {
                console.error("File is not a File object:", file);
                message.error("Invalid file format. Please select a new file.");
                setLoading(false);
                return;
            }

            const response = editRecord
                ? await dispatch(InstallationGuide.updateGuidelines(editRecord.id, formData))
                : await dispatch(InstallationGuide.postGuidelines(formData));

            if (response?.status === 'success') {
                message.success(response.message || (editRecord ? "Updated successfully." : "Created successfully."));
                resetForm();
                setModalVisible(false);
                setRefreshKey(prev => prev + 1);
                setPage(1);
                fetchGuidelines();
            } else {
                message.error(response?.message || "Failed to save.");
            }
        } catch (err) {
            console.error("Error:", err);
            message.error("An error occurred while saving.");
        } finally {
            setLoading(false);
        }
    };

    const uploadProps = {
        accept: '.pdf,.doc,.docx',
        name: 'file',
        maxCount: 1,
        fileList,
        beforeUpload: (file) => {
            const isAllowedType = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            ].includes(file.type);

            if (!isAllowedType) {
            message.error('You can only upload PDF, DOC, or DOCX files!');
            return Upload.LIST_IGNORE;
            }

            const isLt10MB = file.size / 1024 / 1024 <= 10;
            if (!isLt10MB) {
            message.error('File must be smaller than 10MB!');
            return Upload.LIST_IGNORE;
            }
            setFile(file);
            setFileChanged(true);
            setFileError('');
            return false; 
        },
        onRemove: () => {
            setFile(null);
            setFileList([]);
            setFileChanged(true);
        },
        onChange: ({ file, fileList }) => {
            if (file && file.originFileObj) {
                setFile(file.originFileObj);
                setFileChanged(true);
                console.log("File changed via upload:", file.originFileObj);
            } else if (file && file.status === 'removed') {
                setFile(null);
                setFileChanged(true);
            }
            setFileList(fileList);
        }
    };

    const handleAdd = () => {
            setEditRecord(null);
            resetForm();
            setModalVisible(true);
    };

    const handleEdit = async(record) => {
        setEditRecord(record);
        setManufacturerName(record.manufacturer || '');
        setStyleName(record.style_number || '');
        setFormMonth(record.month || '');
        setFormYear(record.year || '');
        setFileChanged(false);

        // Handle existing file
        if (record.s3_bucket_path) {
            const fileName = record.s3_bucket_path.split('/').pop();
            setFileList([
                {
                    uid: '-1',
                    name: fileName,
                    status: 'done',
                    url: record.s3_bucket_path,
                },
            ]);
            setFile(null);
        } else {
            setFileList([]);
            setFile(null);
        }

        setModalVisible(true);
    };


   const handleDelete = async(record) => {
        Modal.confirm({
            title: "Are you sure?",
            content: "This will permanently delete the guideline.",
            okText: "Yes, Delete",
            cancelText: "Cancel",
            onOk: async () => {
            try {
                const res = await dispatch(InstallationGuide.DeleteGuidelines(record.id));
                if (res?.status === "success") {
                message.success(res.message || "Deleted successfully");
                setRefreshKey(prev => prev + 1); 
                setPage(1)
                fetchGuidelines()
                } else {
                message.error(res?.message || "Failed to delete");
                }
            } catch (err) {
                console.error("Delete error:", err);
                message.error("An error occurred while deleting");
            }
            }
        });
    };

    const resetForm = () => {
        setManufacturerName('');
        setStyleName('');
        setFormMonth('');
        setFormYear('');
        setFile(null);
        setFileList([]);
        setFileChanged(false);
        setManufacturerError('');
        setFileError('');
    };

    

    return (
        <div className="tableWrapper">
            <div className="filterWrap">
                <Row gutter={16} justify="space-between">
                   <Col md={8} xs={24}>
                        <div className="searchWrapper">
                            {is_admin && (
                            <Input
                                className="formControl"
                                prefix={<SearchOutlined />}
                                allowClear
                                placeholder="Search..."
                                onChange={(e) => {
                                setSearchedText(e.target.value);
                                }}
                            />
                            )}
                        </div>
                        </Col>
                        <Col md={12} xs={24}>
                        <div className='text-end'>
                            {is_admin && (
                            <Button
                            type='success'
                            className="btn inspectionAdd" 
                            onClick={() => handleAdd()}
                            icon={<PlusOutlined />}
                            >
                            Add New Guide
                            </Button>
                            )}
                        </div>     
                    </Col>

                    {/* Removed the conditional filter button */}
                    <Col md={24}>
                        {/* The filter section is now always visible for all users */}
                        <div className="filterSection">
                            <Card className="filterCard">
                                <Row gutter={24} justify="center">
                                    <Col span={5}>
                                        <div className="formGroup">
                                            <label>Manufacturer</label>
                                            <Select
                                                placeholder="Select Manufacturer"
                                                onChange={setManufacturer}
                                                value={manufacturer}
                                                style={{ width: "100%" }}
                                            >
                                                <Option value="">Select Manufacturer</Option>
                                                {manufacturerOptions.map(option => (
                                                    <Option key={option} value={option}>{option}</Option>
                                                ))}
                                            </Select>
                                        </div>
                                    </Col>
                                    <Col span={5}>
                                        <div className="formGroup">
                                            <label>Style</label>
                                            <Select
                                                placeholder="Select Style"
                                                onChange={setStyle}
                                                value={style}
                                                style={{ width: "100%" }}
                                            >
                                                <Option value="">Select Style</Option>
                                                {styleOptions.map(option => (
                                                    <Option key={option.id} value={option.name}>{option.name}</Option>
                                                ))}
                                            </Select>
                                        </div>
                                    </Col>
                                    <Col span={5}>
                                        <div className="formGroup">
                                            <label>Month</label>
                                            <Select
                                                placeholder="Select Month"
                                                onChange={setMonth}
                                                value={month}
                                                style={{ width: "100%" }}
                                            >
                                                <Option value="">Select Month</Option>
                                                <Option key="1" value="1">(01) Jan</Option>
                                                <Option key="2" value="2">(02) Feb</Option>
                                                <Option key="3" value="3">(03) Mar</Option>
                                                <Option key="4" value="4">(04) Apr</Option>
                                                <Option key="5" value="5">(05) May</Option>
                                                <Option key="6" value="6">(06) Jun</Option>
                                                <Option key="7" value="7">(07) Jul</Option>
                                                <Option key="8" value="8">(08) Aug</Option>
                                                <Option key="9" value="9">(09) Sep</Option>
                                                <Option key="10" value="10">(10) Oct</Option>
                                                <Option key="11" value="11">(11) Nov</Option>
                                                <Option key="12" value="12">(12) Dec</Option>
                                            </Select>

                                        </div>
                                    </Col>
                                    <Col span={5}>
                                        <div className="formGroup">
                                            <label>Year</label>
                                            <Select
                                                placeholder="Select Year"
                                                onChange={setYear}
                                                value={year}
                                                style={{ width: "100%" }}
                                            >
                                                <Option value="">Select Year</Option>
                                                {[...Array(2025 - 1991 + 1)].map((_, index) => {
                                                    const yearValue = 1991 + index;
                                                    return (
                                                        <Option key={yearValue} value={yearValue.toString()}>
                                                            {yearValue}
                                                        </Option>
                                                    );
                                                })}
                                            </Select>
                                        </div>
                                    </Col>

                                    <Col span={4} className="text-center mt-3" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Button type="primary" class="me-2 my-2 submitBtn" onClick={handleReset} style={{ backgroundColor: '#0a497b' }}>
                                            Reset
                                        </Button>
                                    </Col>
                                </Row>
                            </Card>
                        </div>
                    </Col>
                </Row>
            </div>
            <Table
                bordered
                className="inspectionDataTable"
                tableLayout="auto"
                scroll={{ x: 1000 }}
                columns={loading ? skeletonColumns : columns}
                dataSource={loading ? Array(pageSize).fill({}) : guidelines}
                locale={{
                    emptyText: loading ? <span>&nbsp;</span> : (!is_admin && !manufacturer && !style && !year && !month) ? <Empty description="Please select manufacturer and other filters to view installation guidelines." /> : <Empty />
                }}
                rowKey={(record) => record.id}
                showSorterTooltip={false}
                pagination={{
                    current: page,
                    pageSize: pageSize,
                    onChange: (page, pageSize) => {
                        setPage(page);
                        setPageSize(pageSize);
                    },
                    position: ['bottomCenter'],
                }}
            />

            <Modal
                title={editRecord ? "Edit Installation Guideline" : "Add Installation Guideline"}
                open={modalVisible}
               onCancel={() => {
                setModalVisible(false);
                resetForm();
                }}
                onOk={handleSubmit}
                confirmLoading={loading}
                okText={editRecord ? "Update" : "Submit"}
            >
                <Row gutter={[16, 16]}>
                <Col span={24}>
                    <div className="formGroup">
                        <label className="block text-gray-700 font-medium mb-1">Manufacturer Name <span style={{color: 'red'}}>*</span></label>
                        <Input
                            placeholder="Enter Manufacturer"
                            value={manufacturerName}
                            onChange={(e) => {
                                
                                setManufacturerName(e.target.value);
                            }}
                            className={`rounded-lg h-10 formControl ${manufacturerError ? 'border-red-500' : ''}`}
                        />
                        {manufacturerError && <div style={{ color: 'red', fontSize: '12px' }}>{manufacturerError}</div>}
                    </div>
                </Col>
                 <Col span={24}>
                    <div className="formGroup">
                        <label className="block text-gray-700 font-medium mb-1">Style Name <span style={{color: 'red'}}>*</span></label>
                        <Input
                            placeholder="Enter Style"
                            value={styleName}
                            onChange={(e) => setStyleName(e.target.value)}
                            className={`rounded-lg h-10 formControl ${styleName ? 'border-red-500' : ''}`}
                        />
                    </div>
                </Col>
               <Col md={24}>
                    <div className="formGroup">
                    <label>Month</label>
                    <Select
                        placeholder="Select Month"
                        onChange={setFormMonth}
                        value={FormMonth}
                        style={{ width: "100%" }}
                    >
                        <Option value="">Select Month</Option>
                        <Option key="1" value="1">(01) Jan</Option>
                        <Option key="2" value="2">(02) Feb</Option>
                        <Option key="3" value="3">(03) Mar</Option>
                        <Option key="4" value="4">(04) Apr</Option>
                        <Option key="5" value="5">(05) May</Option>
                        <Option key="6" value="6">(06) Jun</Option>
                        <Option key="7" value="7">(07) Jul</Option>
                        <Option key="8" value="8">(08) Aug</Option>
                        <Option key="9" value="9">(09) Sep</Option>
                        <Option key="10" value="10">(10) Oct</Option>
                        <Option key="11" value="11">(11) Nov</Option>
                        <Option key="12" value="12">(12) Dec</Option>
                    </Select>
                    </div>
                </Col>
                <Col md={24}>
                    <div className="formGroup">
                    <label className="block text-gray-700 font-medium mb-1">Year</label>
                    <Select
                        placeholder="Select Year"
                        value={FormYear}
                        onChange={setFormYear}
                        style={{ width: "100%" }}
                        className="rounded-lg"
                    >
                        <Option value="">Select Year</Option>
                        {[...Array(2025 - 1991 + 1)].map((_, index) => {
                        const yearValue = 1991 + index;
                        return (
                            <Option key={yearValue} value={yearValue.toString()}>
                                {yearValue}
                            </Option>
                        );
                        })}
                    </Select>
                    </div>
                </Col>
                <Col span={24}>
                    <label>PDF File *</label>
                   <Upload
                        {...uploadProps}
                        beforeUpload={() => false}
                        fileList={fileList}
                        onChange={({ file, fileList }) => {
                            setFile(file);
                            setFileList(fileList);
                        }}
                    >
                        <Button icon={<UploadOutlined />}>Select PDF</Button>
                    </Upload>

                     {fileError && <div style={{ color: 'red', fontSize: '12px' }}>{fileError}</div>}
                </Col>
                </Row>
            </Modal>
        </div>
    );
}

export default InstallationGuideDataTables;